/**
 * The line: a discrete-event simulation of lots moving down a route.
 *
 * Every solver in this codebase so far answers a question about ONE wafer at
 * ONE tool: how deep the junction, how flat the film, how much slurry. A fab
 * is not run on those questions. It is run on three numbers that belong to no
 * tool - how much work is in the building (WIP), how fast it comes out
 * (throughput) and how long each lot takes (cycle time) - and on the fact,
 * which nothing at a tool can show, that the three are tied together and that
 * one of them goes non-linear as a tool fills up.
 *
 * WHAT IS SIMULATED. Lots of 25 wafers are released at random intervals and
 * walk the product route in flow/routes.ts, operation by operation, queueing
 * at each tool for a free chamber. Service time at an operation is the
 * route's own wafers-per-hour turned into hours per lot, drawn lognormally
 * around that with a 30% coefficient of variation, which is the standard
 * assumption for a process step. Chambers fail and are repaired on the
 * tool table's MTBF and MTTR, and a run interrupted by a failure resumes
 * when the chamber is back. Nothing here is a new fact about a process; it
 * is the route's facts with a clock and a queue.
 *
 * WHAT IS NOT. No transport time, no operators, no setups, no yield loss.
 * Each of those is real and each would move the numbers, but none changes
 * the shape of the lesson, and a first line that carried all of them would
 * have no way to say which one produced a given curve.
 *
 * THE BUILDING IS SEVERAL BAYS. The floor a student walks is one bay - one
 * etcher, one scanner, one furnace - and a line that size starts a lot every
 * seven hours and carries a dozen lots, which is too few for a queue to mean
 * anything. A fab has many such bays, and its tools are QUALIFIED: a given
 * operation runs on a particular etcher, not on any of them, because each
 * tool is set up and matched for the layers it owns. So each bay's tool is
 * its own queue, a lot's operations are spread across the bays, and the
 * bottleneck is worked out over all of them. Pooling every chamber in the
 * building into one queue was tried first and produced a fab that never
 * queued, because thirty interchangeable servers never do; the dedication is
 * what real fabs have and what puts the hours in.
 *
 * EVERY RANDOM DRAW HAS AN OWNER. Arrivals come from one stream; each lot
 * draws its own service times from a stream seeded by its id; each chamber
 * draws its own failures from a stream seeded by its place in the building.
 * So two runs on the same seed have the same lots, the same service times
 * and the same failures no matter how their events interleave - and a run
 * with an outage can be laid over a run without one, lot by lot, to say
 * which lots the outage delayed and by how much. That comparison is the
 * whole of the scenario's cost line, and it is only honest because nothing
 * else differs.
 *
 * TIME IS IN HOURS throughout, from the moment the line starts empty.
 */

import { expand, type FlowStep, type ProcessFlow } from '../flow/model'
import { LINE_TOOLS, lineToolSpec, type LineToolSpec } from './tools'

/** Wafers per lot. A standard 300 mm FOUP. */
export const LOT_SIZE = 25

/** Coefficient of variation of a service time around the route's mean. */
export const SERVICE_CV = 0.3

/** Bays in the building unless a caller says otherwise - see LineOptions.scale. */
export const DEFAULT_SCALE = 10

// ----------------------------------------------------------------- random

/**
 * mulberry32: small, fast, and good enough for a queue. Seeded so a run is
 * a function of its seed and nothing else - see the module note on owners.
 */
export function rng(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** Mix a seed with a few integers into a new seed, so streams do not collide. */
function subSeed(seed: number, ...parts: number[]): number {
  let h = seed >>> 0
  for (const p of parts) {
    h = Math.imul(h ^ (p + 0x9e3779b9), 0x85ebca6b) >>> 0
    h = (h ^ (h >>> 13)) >>> 0
  }
  return h
}

/** A standard normal by Box-Muller, one draw per call. */
function normal(r: () => number): number {
  let u = 0
  while (u === 0) u = r()
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * r())
}

/** Lognormal with the given mean and coefficient of variation. */
export function lognormal(r: () => number, mean: number, cv: number): number {
  const s2 = Math.log(1 + cv * cv)
  const mu = Math.log(mean) - s2 / 2
  return Math.exp(mu + Math.sqrt(s2) * normal(r))
}

/** Exponential with the given mean. */
export function exponential(r: () => number, mean: number): number {
  let u = 0
  while (u === 0) u = r()
  return -mean * Math.log(u)
}

// ------------------------------------------------------------------ heap

type EventKind = 'release' | 'finish' | 'fail' | 'repair' | 'sample' | 'hook'

interface Event {
  t: number
  seq: number
  kind: EventKind
  tool?: Tool
  chamber?: number
  /** For a finish: which run it belongs to, so a stale one is ignored. */
  token?: number
  fn?: () => void
}

/** A binary min-heap on (t, seq): earliest first, ties in insertion order. */
class EventQueue {
  private heap: Event[] = []
  private seq = 0
  push(e: Omit<Event, 'seq'>): void {
    const ev = { ...e, seq: this.seq++ } as Event
    const h = this.heap
    h.push(ev)
    let i = h.length - 1
    while (i > 0) {
      const p = (i - 1) >> 1
      if (before(h[i], h[p])) {
        ;[h[i], h[p]] = [h[p], h[i]]
        i = p
      } else break
    }
  }
  peek(): Event | undefined {
    return this.heap[0]
  }
  pop(): Event | undefined {
    const h = this.heap
    if (h.length === 0) return undefined
    const top = h[0]
    const last = h.pop()!
    if (h.length > 0) {
      h[0] = last
      let i = 0
      for (;;) {
        const l = 2 * i + 1
        const rr = l + 1
        let m = i
        if (l < h.length && before(h[l], h[m])) m = l
        if (rr < h.length && before(h[rr], h[m])) m = rr
        if (m === i) break
        ;[h[i], h[m]] = [h[m], h[i]]
        i = m
      }
    }
    return top
  }
}
const before = (a: Event, b: Event): boolean => a.t < b.t || (a.t === b.t && a.seq < b.seq)

// ------------------------------------------------------------------ lots

export interface Lot {
  id: number
  /** Which product's route this lot walks - index into Line.routes. */
  product: number
  /** Index into the expanded route: the operation the lot is at or waiting for. */
  step: number
  releasedAt: number
  dueAt: number
  /** A lot with a customer date on it. It jumps every queue, always. */
  hot: boolean
  /** When the lot joined its current queue, for waiting-time stats. */
  queuedAt: number
  finishedAt: number | null
  /** This lot's own service-time draws - see the module note on owners. */
  draw: () => number
  /** When the lot finished its previous operation - the clock a queue-time window runs on. */
  readyAt: number
  /** Times this lot has been sent back for missing a queue-time window. */
  reworks: number
  /** Processed on a tool while it was silently out of control - see LineOptions.excursion. */
  exposed: boolean
}

// ----------------------------------------------------------------- tools

interface Chamber {
  busy: Lot[] | null
  /** Down on the house: the PCW loop it cools through is warm. */
  pcwDown: boolean
  /** Down on its own account: failed, awaiting repair. */
  failed: boolean
  /** Down for planned maintenance, or for the repair an excursion forces. */
  pmDown: boolean
  /** Which failure schedule is live, so a stale draw is ignored. */
  failToken: number
  /** Hours of the current run still to go, frozen while the chamber is down. */
  remainingH: number
  /** Which run the pending finish event belongs to. */
  token: number
  /** Hours this chamber has spent working, for utilisation. */
  busyHours: number
  /** Hours spent down, for either reason. */
  downHours: number
  /** When the current state began. */
  since: number
  /** This chamber's own failure draws. */
  draw: () => number
}

const isDown = (c: Chamber): boolean => c.pcwDown || c.failed || c.pmDown

/**
 * Which lot a tool takes next when a chamber frees up.
 *
 *   fifo       hot lots first, then arrival order. What every tool does with
 *              no further instruction: a hot lot is a lot the fab has agreed
 *              to move ahead of the others, everywhere, or it is not hot.
 *   by-date    the lot nearest its due date first. The rule a supervisor
 *              gives a tool that is behind: hot lots still go first, since
 *              their dates are the tightest, and then whoever can least
 *              afford to wait.
 */
export type Dispatch = 'fifo' | 'by-date'

/** One tool in one bay: its own chambers, its own queue. */
class Tool {
  readonly chambers: Chamber[]
  readonly queue: Lot[] = []
  dispatch: Dispatch = 'fifo'
  /** The batch wake-up already scheduled, so a second one is not. */
  wakeAt = -1
  constructor(
    readonly spec: LineToolSpec,
    readonly bay: number,
    seed: number,
  ) {
    this.chambers = Array.from({ length: spec.chambers }, (_, i) => ({
      busy: null,
      pcwDown: false,
      failed: false,
      pmDown: false,
      failToken: 0,
      remainingH: 0,
      token: 0,
      busyHours: 0,
      downHours: 0,
      since: 0,
      draw: rng(subSeed(seed, 7, bay, i, hashTag(spec.tag))),
    }))
  }
  get tag(): string {
    return this.spec.tag
  }
  /** Lots at this tool right now: queued plus in process. */
  wip(): number {
    return this.queue.length + this.chambers.reduce((n, c) => n + (c.busy?.length ?? 0), 0)
  }
}

function hashTag(tag: string): number {
  let h = 0
  for (let i = 0; i < tag.length; i++) h = (Math.imul(h, 31) + tag.charCodeAt(i)) >>> 0
  return h
}

// ------------------------------------------------------------------ line

export interface LineOptions {
  seed?: number
  /**
   * Load on the bottleneck, 0..1, as a fraction of the capacity it actually
   * has - net of the hours its chambers spend down. The arrival rate is
   * derived from it, so "run the line at 85%" is one number rather than a
   * rate someone has to work out from the route. The bottleneck is found
   * from the route and the tool table, not named.
   */
  load?: number
  /**
   * A lot is due this many times its raw process time after release. 2.2 is
   * a planning norm: a lot that takes 91 hours of processing is promised in
   * about 200.
   */
  dueFactor?: number
  /** Hot lots are promised tighter than the rest - they jump every queue to make it. */
  hotDueFactor?: number
  /** One lot in this many is hot. */
  hotEvery?: number
  /** How often the state is sampled into the series [h]. */
  sampleEveryH?: number
  /** Tool table override, for a harness. */
  tools?: LineToolSpec[]
  /** Bays in the building - see the module note. */
  scale?: number
  /**
   * MORE THAN ONE PRODUCT. Each is a route and a share of the starts. One
   * product's line answers every question in the scenarios; two products'
   * shows what a real fab shows, that the same outage lands differently on
   * a long route and a short one, and that cycle time is a per-product
   * number. The constructor's `flow` is product 0; these add to it.
   */
  products?: Array<{ flow: ProcessFlow; share: number }>
  /** Switch random failures off, for a harness that wants a clean queue. */
  failures?: boolean
  /**
   * QUEUE-TIME WINDOWS. After an exposure the resist has a shelf life: a lot
   * that is not etched within `windowH` hours of leaving the scanner goes
   * back to be stripped and exposed again. Applied to every scanner-then-etch
   * pair on the route. A lot that misses the window is sent back to the
   * exposure step when a chamber would otherwise have taken it, and
   * `Line.reworks` counts it. Nothing on a floor is as dangerous as WIP
   * piling up in front of the second step of such a pair.
   */
  qtimeWindowH?: number
  /**
   * THE GATE: with a window in force, do not START the exposure of a lot
   * that the etcher it is bound for could not etch within the window. The
   * etcher's queue, over the chambers it has UP, is a wait; when that wait
   * exceeds `gateFactor` of the window the lot is held at the scanner - a
   * step whose own queue costs nothing but time - BEFORE the clock starts.
   * That is the one hold that is right, and it is the rule a fab runs: a
   * scanner does not expose what the etcher cannot eat in time.
   *
   * Two forms were tried first and failed the harness. A fixed WIP cap
   * throttled a healthy line, because the cap that prevents a miss during
   * an outage sits below an etcher's everyday queue. Gating only while a
   * chamber is down did nothing, because in a re-entrant line the scanner
   * starves during an etch outage and had almost nothing to hold - the
   * lots that miss are the ones exposed BEFORE the trip. The capacity-
   * aware form catches both: it bites when one chamber is carrying the
   * queue, and it bites on a healthy line at 95% whose etch queue waits
   * past the window on its own.
   */
  gateFactor?: number
  /** A plain cap on the bound-for etcher's WIP, if a caller wants one. 0 for none. */
  gateMaxWip?: number
  /**
   * A SILENT EXCURSION: from `startAt`, one tool in one bay processes every
   * lot wrong without saying so. Lots through it are `exposed`. Nothing
   * stops it but an inspection that happens to sample an exposed lot.
   */
  excursion?: { tag: string; bay: number; startAt: number; repairH: number } | null
  /**
   * SAMPLED INSPECTION at one tool family: each visit is inspected with
   * probability `rate`, costing `extraH` of that tool's time. An inspected
   * lot that is exposed detects the excursion, which takes the tool down
   * for its repair and ends the exposure.
   */
  inspection?: { tag: string; rate: number; extraH: number } | null
}

export interface Sample {
  t: number
  /** Lots in the line. */
  wip: number
  /** Lots queued or in process at the bottleneck tool family. */
  bottleneckWip: number
  /** Lots completed in the previous 24 h. */
  outLast24h: number
  /** Mean cycle time of the lots completed in the previous 24 h [h], or NaN. */
  ctLast24h: number
  /** The same, per product. */
  ctLast24hByProduct: number[]
  /** Lots sent back for a missed queue-time window, cumulative. */
  reworks: number
  /** Lots exposed to a silent excursion, cumulative. */
  exposed: number
}

export interface LineToolReport {
  tag: string
  /** Tools of this kind across the bays. */
  tools: number
  /** Chambers across all of them. */
  chambers: number
  /** Fraction of chamber-hours spent working, over the whole run. */
  utilisation: number
  /** Fraction of chamber-hours spent down, on the house or on a failure. */
  downtime: number
  /** Mean hours a lot waited in a queue of this kind. */
  meanWaitH: number
  /** Lot-visits served. */
  visits: number
}

/**
 * The line itself. Build one from a route, then `run` it to a time; the
 * scenario in scenario.ts schedules the outage and the decisions on top.
 */
export class Line {
  /** Product 0's route - the one the scenarios describe. */
  readonly steps: FlowStep[]
  /** Every product's route, product 0 first. */
  readonly routes: FlowStep[][]
  /** Each product's share of the starts, summing to one. */
  readonly shares: number[]
  /** Product 0's raw process time [h]. */
  readonly rawHoursPerLot: number
  /** Every product's. */
  readonly rawHoursByProduct: number[]
  /** Bays in the building - every tool family has this many tools. */
  readonly scale: number
  /** Asset tag of the tool family with the highest load per lot against its capacity. */
  readonly bottleneck: string
  /** The bottleneck's availability, MTBF / (MTBF + MTTR): the capacity it really has. */
  readonly availability: number
  /** Lots released per hour. */
  readonly arrivalRate: number
  readonly samples: Sample[] = []
  readonly completed: Lot[] = []
  readonly lots: Lot[] = []
  /** Every tool in the building, by family. */
  private readonly families = new Map<string, Tool[]>()
  /** For each product, for each route step, its ordinal among the steps at the same tool family. */
  private readonly stepOrdinal: number[][]
  /** Steps (keyed by product and index) that must start within the queue-time window, mapped to the step a miss goes back to. */
  private readonly qtimeBack = new Map<number, number>()
  /** One integer per (product, step) for the maps above. */
  private stepKey(product: number, step: number): number {
    return product * 100000 + step
  }
  /** For a window pair's second tool family, the first's - who waits on whom. */
  private readonly gateFrom = new Map<string, Set<string>>()
  /** Lots sent back for missing a window, and the hours of work it cost them. */
  reworks = 0
  reworkHours = 0
  /** The excursion, if any: whether it is live, when it was caught, and its toll. */
  excursionLive = false
  detectedAt: number | null = null
  exposedLots = 0
  /** Exposed lots that finished the route before the excursion was caught. */
  escapedLots = 0
  inspections = 0
  /** Lots released but not started: the release hold in effect. */
  private held: Lot[] = []
  private holdStarts = false
  private time = 0
  private nextLotId = 1
  private readonly events = new EventQueue()
  /** The arrivals stream. */
  private readonly arrivals: () => number
  private readonly opts: Required<LineOptions>
  private readonly waits = new Map<string, { n: number; hours: number }>()

  constructor(flow: ProcessFlow, options: LineOptions = {}) {
    this.opts = {
      seed: options.seed ?? 1,
      load: options.load ?? 0.85,
      dueFactor: options.dueFactor ?? 2.2,
      hotDueFactor: options.hotDueFactor ?? 1.6,
      hotEvery: options.hotEvery ?? 8,
      sampleEveryH: options.sampleEveryH ?? 1,
      tools: options.tools ?? LINE_TOOLS,
      scale: options.scale ?? DEFAULT_SCALE,
      failures: options.failures ?? true,
      qtimeWindowH: options.qtimeWindowH ?? 0,
      gateMaxWip: options.gateMaxWip ?? 0,
      gateFactor: options.gateFactor ?? 0,
      excursion: options.excursion ?? null,
      inspection: options.inspection ?? null,
      products: options.products ?? [],
    }
    this.scale = this.opts.scale
    this.arrivals = rng(subSeed(this.opts.seed, 1))
    const extra = this.opts.products
    const extraShare = extra.reduce((a, p) => a + p.share, 0)
    this.routes = [expand(flow), ...extra.map((p) => expand(p.flow))]
    this.shares = [Math.max(0, 1 - extraShare), ...extra.map((p) => p.share)]
    this.steps = this.routes[0]
    this.rawHoursByProduct = this.routes.map((r) => r.reduce((h, s) => h + LOT_SIZE / s.wafersPerHour, 0))
    this.rawHoursPerLot = this.rawHoursByProduct[0]

    // Hours of work one lot brings to each tool family - the mix's average,
    // share-weighted - against the family's capacity to do it across the
    // building. The largest ratio is the bottleneck; the arrival rate that
    // loads it to `load` of the capacity it has when its chambers are up is
    // the line's rate.
    const hoursPerLot = new Map<string, number>()
    this.stepOrdinal = this.routes.map((route, p) => {
      const seen = new Map<string, number>()
      return route.map((s) => {
        hoursPerLot.set(s.tool, (hoursPerLot.get(s.tool) ?? 0) + (this.shares[p] * LOT_SIZE) / s.wafersPerHour)
        const k = seen.get(s.tool) ?? 0
        seen.set(s.tool, k + 1)
        return k
      })
    })
    let worst = 0
    let worstTag = ''
    let worstAvail = 1
    for (const [tag, hours] of hoursPerLot) {
      const spec = this.opts.tools.find((t) => t.tag === tag) ?? lineToolSpec(tag)
      const avail = this.opts.failures && spec.mtbfH && spec.mttrH ? spec.mtbfH / (spec.mtbfH + spec.mttrH) : 1
      // One per bay, unless the building has a count of its own (tools.ts, `units`).
      const units = spec.units ?? this.scale
      const perLot = hours / (units * spec.chambers * (spec.batch ?? 1) * avail)
      if (perLot > worst) {
        worst = perLot
        worstTag = tag
        worstAvail = avail
      }
      const tools: Tool[] = []
      for (let b = 0; b < units; b++) {
        const tool = new Tool(spec, b, this.opts.seed)
        tools.push(tool)
        if (this.opts.failures && spec.mtbfH && spec.mttrH) {
          for (let c = 0; c < tool.chambers.length; c++) {
            this.events.push({ t: exponential(tool.chambers[c].draw, spec.mtbfH), kind: 'fail', tool, chamber: c, token: 0 })
          }
        }
      }
      this.families.set(tag, tools)
      this.waits.set(tag, { n: 0, hours: 0 })
    }
    this.bottleneck = worstTag
    this.availability = worstAvail
    this.arrivalRate = this.opts.load / worst

    // Every scanner-then-etch pair on the route carries the window.
    if (this.opts.qtimeWindowH > 0) {
      this.routes.forEach((route, p) => {
        for (let i = 0; i + 1 < route.length; i++) {
          if (route[i].tool.startsWith('LITH-03') && route[i + 1].tool === 'ETCH-01') {
            this.qtimeBack.set(this.stepKey(p, i + 1), i)
            const set = this.gateFrom.get(route[i + 1].tool) ?? new Set<string>()
            set.add(route[i].tool)
            this.gateFrom.set(route[i + 1].tool, set)
          }
        }
      })
    }
    if (this.opts.excursion) {
      const ex = this.opts.excursion
      this.events.push({ t: ex.startAt, kind: 'hook', fn: () => (this.excursionLive = true) })
    }

    this.events.push({ t: exponential(this.arrivals, 1 / this.arrivalRate), kind: 'release' })
    this.events.push({ t: this.opts.sampleEveryH, kind: 'sample' })
  }

  now(): number {
    return this.time
  }

  /** Lots in the line right now, held ones included. */
  wip(): number {
    return this.lots.length - this.completed.length
  }

  /** The tool table entry for a family. */
  spec(tag: string): LineToolSpec {
    return this.family(tag)[0].spec
  }

  /** Every tool family on this line's routes. */
  familyTags(): string[] {
    return [...this.families.keys()]
  }

  /** How many tools the building has in a family. */
  familySize(tag: string): number {
    return this.family(tag).length
  }

  /** Lots at a tool family right now, queued or in process, across the bays. */
  wipAt(tag: string): number {
    return this.family(tag).reduce((n, t) => n + t.wip(), 0)
  }

  /** Set the dispatch rule at every tool of a family. */
  setDispatch(tag: string, rule: Dispatch): void {
    for (const t of this.family(tag)) t.dispatch = rule
  }

  /**
   * Stop starting new lots, or start them again. A hold on starts is the
   * one hold that changes the numbers: a lot held at the step before a
   * blocked tool is in the same queue by another name, and the model shows
   * it as such. A lot not released is work the building does not have to
   * carry - and output it will not get back.
   */
  setHoldStarts(hold: boolean): void {
    this.holdStarts = hold
    if (!hold) {
      const held = this.held
      this.held = []
      for (const lot of held) this.enqueue(lot)
    }
  }

  /**
   * Take one chamber index out of service on the house, on every tool of a
   * family - a house fault reaches every bay at once. A run in progress
   * waits in the chamber and resumes when it is back, as a wafer does when
   * a chamber loses its setpoint.
   */
  setChamberDown(tag: string, chamber: number, down: boolean): void {
    for (const tool of this.family(tag)) {
      const c = tool.chambers[chamber]
      if (!c || c.pcwDown === down) continue
      if (down) this.goDown(tool, c, () => (c.pcwDown = true))
      else this.comeUp(tool, c, () => (c.pcwDown = false))
    }
    // A scanner gated on this family's queue (LineOptions.gateFactor) has
    // lots waiting that nothing else will wake.
    if (!down) this.wakeGated(tag)
  }

  /**
   * Scanners gated on a family's queue re-try when that queue moves: a
   * chamber back up, or a lot leaving. The families that gate on each other
   * come from the route's window pairs.
   */
  private wakeGated(toTag: string): void {
    if (this.opts.gateFactor <= 0 && this.opts.gateMaxWip <= 0) return
    const from = this.gateFrom.get(toTag)
    if (!from) return
    for (const tag of from) for (const t of this.family(tag)) this.tryStart(t)
  }

  /** Run a function at a model time. */
  at(t: number, fn: () => void): void {
    this.events.push({ t, kind: 'hook', fn })
  }

  /** Advance to a model time. */
  run(untilH: number): void {
    for (;;) {
      const next = this.events.peek()
      if (!next || next.t > untilH) break
      const e = this.events.pop()!
      this.time = e.t
      switch (e.kind) {
        case 'release':
          this.release()
          this.events.push({ t: this.time + exponential(this.arrivals, 1 / this.arrivalRate), kind: 'release' })
          break
        case 'finish':
          this.finish(e.tool!, e.chamber!, e.token!)
          break
        case 'fail': {
          const c = e.tool!.chambers[e.chamber!]
          if (e.token !== c.failToken) break
          // A chamber already down - failed, or in a planned service, or on
          // the house - cannot fail again just now: the draw stands and
          // comes back in an hour. (Dropping it would break the chain of
          // draws, and the control would then have a failure this run lost.)
          if (isDown(c)) {
            this.events.push({ t: this.time + 1, kind: 'fail', tool: e.tool, chamber: e.chamber, token: e.token })
            break
          }
          this.goDown(e.tool!, c, () => (c.failed = true))
          this.events.push({ t: this.time + exponential(c.draw, e.tool!.spec.mttrH!), kind: 'repair', tool: e.tool, chamber: e.chamber })
          break
        }
        case 'repair': {
          const c = e.tool!.chambers[e.chamber!]
          this.comeUp(e.tool!, c, () => (c.failed = false))
          c.failToken++
          this.events.push({ t: this.time + exponential(c.draw, e.tool!.spec.mtbfH!), kind: 'fail', tool: e.tool, chamber: e.chamber, token: c.failToken })
          break
        }
        case 'sample':
          this.sample()
          this.events.push({ t: this.time + this.opts.sampleEveryH, kind: 'sample' })
          break
        case 'hook':
          e.fn?.()
          break
      }
    }
    this.time = untilH
  }

  /** Per-family utilisation and waiting, over the run so far. */
  toolReport(): LineToolReport[] {
    const out: LineToolReport[] = []
    for (const [tag, tools] of this.families) {
      let busy = 0
      let down = 0
      let chambers = 0
      for (const tool of tools) {
        for (const c of tool.chambers) {
          this.account(c)
          busy += c.busyHours
          down += c.downHours
          chambers++
        }
      }
      const hours = chambers * this.time
      const w = this.waits.get(tag)!
      out.push({
        tag,
        tools: tools.length,
        chambers,
        utilisation: hours > 0 ? busy / hours : 0,
        downtime: hours > 0 ? down / hours : 0,
        meanWaitH: w.n > 0 ? w.hours / w.n : 0,
        visits: w.n,
      })
    }
    return out.sort((a, b) => b.utilisation - a.utilisation)
  }

  // ------------------------------------------------------------ internals

  private family(tag: string): Tool[] {
    const f = this.families.get(tag)
    if (!f) throw new Error(`no tool ${tag} on this line`)
    return f
  }

  /**
   * The one tool in the building that runs a given lot's given operation.
   * Spread by operation AND by lot: a family with thirty-one operations per
   * lot shares them across the bays, and a family with one operation per
   * lot (the prober) still sees every bay used, by different lots.
   */
  private toolFor(lot: Lot): Tool {
    return this.toolForStep(lot, lot.step)
  }

  private toolForStep(lot: Lot, step: number): Tool {
    const tools = this.family(this.routes[lot.product][step].tool)
    return tools[(this.stepOrdinal[lot.product][step] + lot.id) % tools.length]
  }

  /** The operation a lot is at, on its own product's route. */
  private stepOf(lot: Lot, step = lot.step): FlowStep {
    return this.routes[lot.product][step]
  }

  private release(): void {
    const id = this.nextLotId++
    const hot = id % this.opts.hotEvery === 0
    let product = 0
    if (this.routes.length > 1) {
      let u = this.arrivals()
      for (let p = 0; p < this.shares.length; p++) {
        u -= this.shares[p]
        if (u < 0) {
          product = p
          break
        }
      }
    }
    const lot: Lot = {
      id,
      product,
      step: 0,
      releasedAt: this.time,
      dueAt: this.time + this.rawHoursByProduct[product] * (hot ? this.opts.hotDueFactor : this.opts.dueFactor),
      hot,
      queuedAt: this.time,
      finishedAt: null,
      draw: rng(subSeed(this.opts.seed, 3, id)),
      readyAt: this.time,
      reworks: 0,
      exposed: false,
    }
    this.lots.push(lot)
    if (this.holdStarts) this.held.push(lot)
    else this.enqueue(lot)
  }

  private enqueue(lot: Lot): void {
    const tool = this.toolFor(lot)
    lot.queuedAt = this.time
    tool.queue.push(lot)
    this.tryStart(tool)
  }

  /** Fill every free chamber at a tool from its queue. */
  private tryStart(tool: Tool): void {
    for (let i = 0; i < tool.chambers.length; i++) {
      const c = tool.chambers[i]
      if (isDown(c) || c.busy) continue
      const lots = this.pick(tool)
      if (!lots) return
      const w = this.waits.get(tool.tag)!
      for (const lot of lots) {
        w.n++
        w.hours += this.time - lot.queuedAt
      }
      const step = this.stepOf(lots[0])
      let hours = lognormal(lots[0].draw, LOT_SIZE / step.wafersPerHour, SERVICE_CV)
      // Sampled inspection: decided as the lot goes on, from its own stream.
      const insp = this.opts.inspection
      if (insp && step.tool === insp.tag && lots[0].draw() < insp.rate) {
        hours += insp.extraH
        this.inspections++
        if (lots[0].exposed && this.excursionLive) this.detect()
      }
      this.account(c)
      c.busy = lots
      c.remainingH = hours
      c.token++
      this.events.push({ t: this.time + hours, kind: 'finish', tool, chamber: i, token: c.token })
    }
  }

  /** Which lot goes next, by the tool's rule. Hot lots first under either. */
  private next(tool: Tool): number {
    const q = tool.queue
    const hot = q.findIndex((l) => l.hot)
    if (tool.dispatch === 'fifo') return hot >= 0 ? hot : 0
    let best = 0
    for (let i = 1; i < q.length; i++) if (q[i].dueAt < q[best].dueAt) best = i
    return best
  }

  /**
   * The lots the next free chamber takes, or null if it should wait.
   *
   * A single-lot tool takes one, by its dispatch rule. A batch tool takes
   * up to a boatful of lots at the SAME operation - a furnace runs one
   * recipe per boat - and only when the boat is full or the oldest lot at
   * that operation has waited long enough (tools.ts).
   */
  private pick(tool: Tool): Lot[] | null {
    const q = tool.queue
    if (q.length === 0) return null
    const batch = tool.spec.batch ?? 1
    if (batch <= 1) {
      // Candidates in dispatch order; a lot can be sent back (window missed)
      // or passed over (gated) and the next one considered.
      for (;;) {
        if (q.length === 0) return null
        const i = this.next(tool)
        const lot = q[i]
        const back = this.qtimeBack.get(this.stepKey(lot.product, lot.step))
        if (back !== undefined && this.time - lot.readyAt > this.opts.qtimeWindowH) {
          // Missed the window: strip and re-expose. Back to the scanner's
          // queue, charged the scanner's hours again plus a strip.
          q.splice(i, 1)
          lot.reworks++
          this.reworks++
          this.reworkHours += LOT_SIZE / this.stepOf(lot, back).wafersPerHour + 0.5
          lot.step = back
          this.enqueue(lot)
          continue
        }
        if ((this.opts.gateMaxWip > 0 || this.opts.gateFactor > 0) && this.qtimeBack.has(this.stepKey(lot.product, lot.step + 1))) {
          // Gated: the etcher this lot is bound for could not etch it within
          // the window. Take the first lot in the queue that is not, or
          // nothing.
          const gated = (l: Lot): boolean => {
            if (!this.qtimeBack.has(this.stepKey(l.product, l.step + 1))) return false
            const next = this.toolForStep(l, l.step + 1)
            if (this.opts.gateMaxWip > 0 && next.wip() > this.opts.gateMaxWip) return true
            if (this.opts.gateFactor > 0) {
              const up = next.chambers.filter((c) => !isDown(c)).length
              const serviceH = LOT_SIZE / this.stepOf(l, l.step + 1).wafersPerHour
              const waitH = up === 0 ? Infinity : (next.wip() * serviceH) / up
              return waitH > this.opts.gateFactor * this.opts.qtimeWindowH
            }
            return false
          }
          if (gated(lot)) {
            const other = q.findIndex((l) => !gated(l))
            if (other < 0) return null
            return q.splice(other, 1)
          }
        }
        return q.splice(i, 1)
      }
    }
    // Batch: the operation of the lot that would go next decides the recipe.
    const lead = q[this.next(tool)]
    const same = q.filter((l) => l.step === lead.step)
    const maxWait = tool.spec.batchMaxWaitH ?? 0
    const oldest = same.reduce((a, l) => Math.min(a, l.queuedAt), Infinity)
    const wakeAt = oldest + maxWait
    // With a tolerance: the wake-up lands at queuedAt + maxWait, and in
    // floating point (queuedAt + maxWait) - queuedAt is not always maxWait.
    // Without it the wake-up found the lot a rounding error short of ready,
    // scheduled itself again at the same instant, and the clock never moved.
    if (same.length < batch && this.time + 1e-9 < wakeAt) {
      // Not yet: come back when the oldest has waited its limit. One
      // wake-up per waiting lot, not one per call - every enqueue upstream
      // calls tryStart here, and each used to leave another hook behind.
      if (tool.wakeAt !== wakeAt) {
        tool.wakeAt = wakeAt
        this.events.push({ t: wakeAt, kind: 'hook', fn: () => this.tryStart(tool) })
      }
      return null
    }
    const taken = same.slice(0, batch)
    for (const l of taken) q.splice(q.indexOf(l), 1)
    return taken
  }

  private finish(tool: Tool, chamber: number, token: number): void {
    const c = tool.chambers[chamber]
    // A stale finish: the chamber went down mid-run and the run was
    // rescheduled with a new token when it came back.
    if (!c.busy || c.token !== token) return
    this.account(c)
    const lots = c.busy
    c.busy = null
    c.remainingH = 0
    const ex = this.opts.excursion
    for (const lot of lots) {
      if (ex && this.excursionLive && tool.tag === ex.tag && tool.bay === ex.bay && !lot.exposed) {
        lot.exposed = true
        this.exposedLots++
      }
      lot.readyAt = this.time
      lot.step++
      if (lot.step >= this.routes[lot.product].length) {
        lot.finishedAt = this.time
        if (lot.exposed && this.detectedAt === null) this.escapedLots++
        this.completed.push(lot)
      } else {
        this.enqueue(lot)
      }
    }
    this.tryStart(tool)
    this.wakeGated(tool.tag)
  }

  /** An inspection has found an exposed lot: the bad tool comes down for its repair. */
  private detect(): void {
    const ex = this.opts.excursion!
    this.excursionLive = false
    this.detectedAt = this.time
    const bad = this.family(ex.tag)[ex.bay]
    for (const c of bad.chambers) this.goDown(bad, c, () => (c.pmDown = true))
    this.events.push({
      t: this.time + ex.repairH,
      kind: 'hook',
      fn: () => {
        for (const c of bad.chambers) this.comeUp(bad, c, () => (c.pmDown = false))
      },
    })
  }

  /**
   * Planned maintenance on one chamber of one tool: down for `hours` from
   * `at`. The service resets its hazard to the table's.
   */
  schedulePm(tag: string, bay: number, chamber: number, at: number, hours: number): void {
    const tool = this.family(tag)[bay]
    const c = tool.chambers[chamber]
    this.events.push({ t: at, kind: 'hook', fn: () => this.goDown(tool, c, () => (c.pmDown = true)) })
    this.events.push({ t: at + hours, kind: 'hook', fn: () => this.comeUp(tool, c, () => (c.pmDown = false)) })
  }

  /**
   * Maintenance deferred: from `at`, the worn chamber carries ONE extra
   * failure, drawn from its own stream with mean `mtbfH`, on top of the
   * schedule it shares with the control - so the two runs differ by that
   * failure and nothing else. When it comes it takes `mttrH` to repair,
   * longer than the table's, because a worn chamber that fails in service
   * is not a four-hour job; and that repair is the service finally done.
   * `wearFailedAt` records it, or stays null if the week ended first.
   */
  deferPm(tag: string, bay: number, chamber: number, at: number, mtbfH: number, mttrH: number): void {
    const tool = this.family(tag)[bay]
    const c = tool.chambers[chamber]
    const wear = rng(subSeed(this.opts.seed, 11, bay, chamber))
    const failAt = at + exponential(wear, mtbfH)
    this.events.push({
      t: failAt,
      kind: 'hook',
      fn: () => {
        if (isDown(c)) {
          // Already down for something else: the wear failure lands as soon
          // as it is back up. Try again in an hour.
          this.events.push({ t: this.time + 1, kind: 'hook', fn: () => this.wearFail(tool, c, mttrH) })
          return
        }
        this.wearFail(tool, c, mttrH)
      },
    })
  }

  /** When the deferred chamber finally failed, or null. */
  wearFailedAt: number | null = null

  private wearFail(tool: Tool, c: Chamber, mttrH: number): void {
    if (isDown(c)) {
      this.events.push({ t: this.time + 1, kind: 'hook', fn: () => this.wearFail(tool, c, mttrH) })
      return
    }
    this.wearFailedAt = this.time
    this.goDown(tool, c, () => (c.pmDown = true))
    this.events.push({ t: this.time + mttrH, kind: 'hook', fn: () => this.comeUp(tool, c, () => (c.pmDown = false)) })
  }

  /** Hours one chamber has been down, for any reason, so far. */
  chamberDownHours(tag: string, bay: number, chamber: number): number {
    const c = this.family(tag)[bay].chambers[chamber]
    this.account(c)
    return c.downHours
  }

  /** A chamber goes down for some reason; a run in it freezes where it is. */
  private goDown(tool: Tool, c: Chamber, mark: () => void): void {
    const wasDown = isDown(c)
    this.account(c)
    mark()
    if (wasDown) return
    if (c.busy) {
      // Freeze the run: what was left to do is what will be left to do.
      c.remainingH = Math.max(0, c.remainingH - (this.time - c.since))
      c.token++
    }
    c.since = this.time
    void tool
  }

  /** One reason for being down is cleared; if none remain, the run resumes. */
  private comeUp(tool: Tool, c: Chamber, clear: () => void): void {
    this.account(c)
    clear()
    if (isDown(c)) return
    if (c.busy) {
      c.token++
      this.events.push({ t: this.time + c.remainingH, kind: 'finish', tool, chamber: tool.chambers.indexOf(c), token: c.token })
    } else {
      this.tryStart(tool)
    }
    this.wakeGated(tool.tag)
  }

  /**
   * Charge a chamber's time since its last state change to the right
   * bucket. A run's remaining hours are also kept current here, so that a
   * failure part-way through knows how much was done.
   */
  private account(c: Chamber): void {
    const dt = this.time - c.since
    if (dt > 0) {
      if (isDown(c)) c.downHours += dt
      else if (c.busy) {
        c.busyHours += dt
        c.remainingH = Math.max(0, c.remainingH - dt)
      }
    }
    c.since = this.time
  }

  private sample(): void {
    const since = this.time - 24
    let n = 0
    let ct = 0
    const np = this.routes.map(() => 0)
    const ctp = this.routes.map(() => 0)
    for (let i = this.completed.length - 1; i >= 0; i--) {
      const lot = this.completed[i]
      if (lot.finishedAt! < since) break
      n++
      ct += lot.finishedAt! - lot.releasedAt
      np[lot.product]++
      ctp[lot.product] += lot.finishedAt! - lot.releasedAt
    }
    this.samples.push({
      t: this.time,
      wip: this.wip(),
      bottleneckWip: this.wipAt(this.bottleneck),
      outLast24h: n,
      ctLast24h: n > 0 ? ct / n : NaN,
      ctLast24hByProduct: np.map((k, p) => (k > 0 ? ctp[p] / k : NaN)),
      reworks: this.reworks,
      exposed: this.exposedLots,
    })
  }
}

/** Mean of a series between two times, over a field. */
export function meanOver(samples: Sample[], from: number, to: number, field: 'wip' | 'bottleneckWip' | 'outLast24h' | 'ctLast24h' | 'reworks' | 'exposed'): number {
  let n = 0
  let sum = 0
  for (const s of samples) {
    if (s.t < from || s.t > to) continue
    const v = s[field]
    if (Number.isNaN(v)) continue
    n++
    sum += v
  }
  return n > 0 ? sum / n : NaN
}

/** Mean cycle time of the lots completed between two times [h]. */
export function meanCycleTime(line: Line, from: number, to: number): number {
  let n = 0
  let sum = 0
  for (const lot of line.completed) {
    const f = lot.finishedAt!
    if (f < from || f > to) continue
    n++
    sum += f - lot.releasedAt
  }
  return n > 0 ? sum / n : NaN
}
